import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { setOrderAction } from '../../actions/SetOrderAction';
import { enterSpace } from '~src/app/actions/spaces/EnterSpaceAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setContentFilterNoRequestContent } from '../../actions/spaces/filters/FilterActions';
import { MapDispatchToProps } from '~src/app/utils/ConnectUtils';
import { IOrderClause } from '~src/ts/ServerApi/types/IOrderClause';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { CollectionsSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { ConnectedQueryParamUpdateProvider } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { AllCollectionsSpaceOwnProps } from './AllCollectionsSpace';
import { ConnectedAllCollectionsSpace } from './ConnectedAllCollectionsSpace';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '~src/ts/models/sort/SortOptionDefinitions';
import { getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { UrlParamKeys } from '~src/app/UrlParamKeys';
import { ContentFilterKeyName, ContentFiltersKeys } from '~src/ts/models/ContentFilters';
import { takeFirstInstanceOf } from '~src/ts/helpers/UrlHelpers';

export interface AllCollectionsSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly subspaceName: CollectionsSubspaceName;
  readonly renderSpace: (props: AllCollectionsSpaceOwnProps) => JSX.Element;
}

export interface AllCollectionsSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
}

export type AllCollectionsSpaceProviderContainerProps = AllCollectionsSpaceProviderOwnProps & AllCollectionsSpaceProviderDispatchProps;

export class AllCollectionsSpaceProviderContainer extends React.PureComponent<AllCollectionsSpaceProviderContainerProps> {
  public render() {
    const props = {
      angularContext: this.props.angularContext,
      urls: this.props.urls
    };
    return this.props.renderSpace(props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: AllCollectionsSpaceProviderContainerProps) {
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    this.props.enterSpace(
      {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'collections',
        subspaceName: this.props.subspaceName
      }
    );

    this.setFilterTypesFromUrl();
    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();
  }

  private clearAndSetSortOrderFromURL() {
    const spaceInfo: SpaceInfo = {
      item: SpaceItemForNonEntitySpace,
      spaceType: 'collections',
      subspaceName: this.props.subspaceName
    };
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(['collection'], spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private setFilterTypesFromUrl() {
    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
    for (const filterType of _.keys(ContentFiltersKeys)) {
      const value = takeFirstInstanceOf(currentQueryValues[filterType]);
      this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value);
    }
  }
}

const mapDispatchToProps: MapDispatchToProps<AllCollectionsSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  setOrderAction,
  setContentFilterNoRequestContent
}, dispatch);

export const ConnectedAllCollectionsSpaceProviderContainer: React.ComponentClass<AllCollectionsSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(AllCollectionsSpaceProviderContainer);

const renderSpace = (props: AllCollectionsSpaceOwnProps): JSX.Element => <ConnectedAllCollectionsSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type AllCollectionsSpaceProviderProps = LocationPathProp & Omit<AllCollectionsSpaceProviderOwnProps, 'renderSpace'>;

export const AllCollectionsSpaceProvider: React.FunctionComponent<AllCollectionsSpaceProviderProps> = (props) => {

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'collections',
    subspaceName: props.subspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={false}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedAllCollectionsSpaceProviderContainer
          routeLocationPath={props.routeLocationPath}
          routeLocationSearch={props.routeLocationSearch}
          renderSpace={renderSpace}
          angularContext={props.angularContext}
          urls={props.urls}
          subspaceName={props.subspaceName}
        />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
